/**
 * Step 3's plans — Support at home on the aged care spine (rebuild, 19 Oct 2026; the agreed mock-up's
 * plans screen, j1-plans).
 *
 *  - The chosen-plan banner, when one is chosen.
 *  - The four numbers from step 2 as a strip, the comparison every card is read against.
 *  - "What matters most to Colin and Shelly?" — the four goals; choosing one never re-runs the engine.
 *  - The cards RANKED BY THE GOAL SELECTED (owner, 18 Oct 2026: the mock-up first showed the
 *    pension and cashflow leaders under "best estate"). Where another goal is led by a different
 *    plan, one line says so and switches to it.
 *  - Each card: the plan in words, the estate figure, their contribution / Age Pension / each year with
 *    "why?", the rate change on the card face (decision 4, 19 Oct: paying less for the care IS the
 *    rates falling), Explain this / What could go wrong / Choose this plan, and the old detail behind
 *    "Show the detail".
 *  - The ways on: compare, every way tested, things that change the answer, build a plan yourself.
 *
 * Presentation only. Every figure is the engine row's own.
 */
import React from 'react';
import EnginePlanCard, { type PlanCardNumber } from '../agedCare/EnginePlanCard.tsx';
import DoNothingStrip from '../agedCare/DoNothingStrip.tsx';
import EngineGoalControl, { type GoalFocusId } from '../agedCare/EngineGoalControl.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { planRunwayCopy } from '../../components/insights/fundingSummary.tsx';
import { goalLeaderFor, NEAR_BEST_LABEL, type RecommendedStrategyEntry } from '../../strategy/supportAtHomeRecommendedStrategies.ts';
import { allPlansOpenLine, secondPlanLine, secondPlanChip, leadChip, leadLine, SECOND_PLAN_CHIP } from '../../strategy/planChoiceRule.ts';
import { CASHFLOW_COVERED_LABEL, coveredIsBestEstate } from '../../strategy/cashflowCoveredGoal.ts';
import { SAH_STRATEGY_EXPLAINERS } from './sahStrategyExplainers.ts';
import { sahPlanWords, type SahConfigLike } from './sahPlanWords.ts';
import { figuresFromSnapshot, type SahFigurePage, type SahFigures } from './SahPlanDialogs.tsx';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { householdVoice } from '../../utils/householdWords.ts';
import { runwayCardLine } from '../agedCare/moneyStory.tsx';
import { SAH_BENCH } from '../../utils/benchmarkWords.ts';
import { ChosenAdviceLine } from '../../components/strategy/ChosenAdviceLine.tsx';

export interface SahPlanRow {
    name?: string;
    config?: SahConfigLike;
    snapshot?: Record<string, unknown>;
    projections?: unknown[];
    recommendationLabels?: string[];
    [key: string]: unknown;
}

export interface SahPlansViewProps {
    factFind: DraftFactFind;
    doNothing: SahPlanRow | null;
    recommended: RecommendedStrategyEntry[];
    /** Every evaluated plan (to find the full row for a leader). */
    strategies: SahPlanRow[];
    waysTested: number;
    horizonYears: number;
    focus: GoalFocusId;
    onFocus: (f: GoalFocusId) => void;
    /** The chosen plan's engine name for THIS run, if one is chosen. */
    chosenName: string | null;
    onChoose: (row: SahPlanRow) => void;
    onExplain: (row: SahPlanRow) => void;
    onRisks: (row: SahPlanRow) => void;
    onWhy: (row: SahPlanRow, page: SahFigurePage) => void;
    onCareUp?: (row: SahPlanRow) => void;
    renderDetail: (row: SahPlanRow) => React.ReactNode;
    onCompare: () => void;
    onAllWays: () => void;
    onSettings: () => void;
    onBuildYourOwn: (row: SahPlanRow | null) => void;
    onAdvice: () => void;
    /** Notices above the strip (service plan changed since the run; linked aged care). */
    notices?: React.ReactNode;
    /**
     * A plan with a guaranteed income (owner, 21 Oct 2026): its family panel, and for income only the
     * client's confirmation before it can be chosen (IncomeOnlyBeforeChoose), with the button held until then.
     */
    guaranteedIncomeFor?: (row: SahPlanRow) => { familyPanel?: React.ReactNode; beforeChoose?: React.ReactNode; chooseHeld?: boolean } | null;
}

const GOAL_CHIP: Record<string, string> = {
    'Optimal Legacy': 'Leaves the most',
    [CASHFLOW_COVERED_LABEL]: 'Income covers the costs',
    'Optimal Cashflow': 'Most left over each year',
    'Optimal Age Pension': 'Highest Age Pension',
    [NEAR_BEST_LABEL]: SECOND_PLAN_CHIP,
};

const pct = (r: number) => `${+(r * 100).toFixed(1)}%`;

/** How much money the plan moves, and what to call it on the second bar. */
export function sahMovedAmount(config: SahConfigLike | undefined): { amount: number; label: string } {
    const c = config || {};
    const of = (x: SahConfigLike): number => Number(x.equityAmount ?? x.renovationAmount ?? x.purchaseAmount ?? x.amount ?? x.repaymentAmount ?? x.loanAmount ?? 0) || 0;
    switch (c.type) {
        case 'increaseHomeEquity': case 'renovate': case 'upgrade': return { amount: of(c), label: 'Moved into the home' };
        case 'annuity': return { amount: of(c), label: 'Locked into the income' };
        case 'gifting': return { amount: of(c), label: 'Given away' };
        case 'funeralBonds': return { amount: of(c), label: 'Put into funeral bonds' };
        case 'debtRepayment': return { amount: of(c), label: 'Used to clear the debt' };
        case 'reverseMortgage': return { amount: of(c), label: 'Borrowed against the home' };
        case 'downsize': return { amount: Number(c.newHomeValue) || 0, label: 'Put into the new home' };
        case 'heas': return { amount: (Number(c.paymentAmount) || 0) * (c.paymentFrequency === 'monthly' ? 12 : 26), label: 'Borrowed each year' };
        case 'combination': return { amount: (c.steps || []).reduce((t, s) => t + of(s), 0), label: 'Money moved' };
        default: return { amount: 0, label: 'Money moved' };
    }
}

export interface SahPlanCardProps {
    /** "Colin and Shelly's" — whose file the plan is saved with. */
    possessiveFile: string;
    /** The client file, for the "When the money runs out" line's accounts (runwayCardLine). */
    factFind?: DraftFactFind;
    /** "you" / "they" and "Your" / "Their" — the household after the first mention (householdVoice). */
    they?: string;
    Their?: string;
    fig: SahFigures;
    base: SahFigures | null;
    title: string;
    sub: string;
    chip: string | null;
    moved: { amount: number; label: string };
    /** The largest gain and amount moved across the cards shown together, so the bars compare. */
    scale?: { maxGain: number; maxMoved: number };
    horizonYears: number;
    projections?: unknown[];
    /** The plan's kind (`config.type`) for its "Worth knowing" line. */
    planType: string;
    chosen: boolean;
    anotherChosen: boolean;
    onExplain: () => void;
    onRisks: () => void;
    onChoose: () => void;
    onWhy: (page: SahFigurePage) => void;
    onAdvice: () => void;
    /** Shown once chosen; absent on a plan already being built by hand. */
    onBuildOn?: () => void;
    onCareUp?: () => void;
    detail?: React.ReactNode;
    detailOpen?: boolean;
    onToggleDetail?: () => void;
    testId?: string;
    /** Why the choose button is held, e.g. while the figures are being worked out. */
    chooseDisabled?: boolean;
    /** The second card's one line: the trade-off that earns it a place (planChoiceRule.ts). */
    tradeOffNote?: string | null;
    /** What must be confirmed before choosing (the income-only confirmation, tested or hand-built), and whether the button is held. */
    beforeChoose?: React.ReactNode;
    chooseHeld?: boolean;
    /**
     * A plan with a guaranteed income: what the family receives from it at the start, middle and end of the
     * period (AnnuityFamilyPanel; owner, 21 Oct 2026, mock-up screens 3 and 4). Part of the card, under the
     * runway line, so it reads as information about the plan.
     */
    familyPanel?: React.ReactNode;
}

/**
 * ONE PLAN CARD for a tested plan and a plan built by hand (owner, 19 Oct 2026: Manual Control keeps
 * the engine's look and feel — the same card, Explain this, What could go wrong, buttons and words).
 * Every figure comes in on `fig` and `base`; the card does no arithmetic beyond differences.
 */
export const SahPlanCard = ({
    possessiveFile, factFind, they = 'they', Their = 'Their', fig, base, title, sub, chip, moved, scale, horizonYears, projections, planType, chosen, anotherChosen,
    onExplain, onRisks, onChoose, onWhy, onAdvice, onBuildOn, onCareUp, detail, detailOpen = false, onToggleDetail, testId = 'sah-plan-card', chooseDisabled = false, tradeOffNote = null, beforeChoose, chooseHeld = false, familyPanel,
}: SahPlanCardProps) => {
    const legacyDelta = base ? fig.legacy - base.legacy : null;
    const maxGain = scale?.maxGain ?? Math.max(1, Math.abs(legacyDelta ?? 0));
    const maxMoved = scale?.maxMoved ?? Math.max(1, moved.amount);
    const rateLine = fig.rates && base?.rates && (Math.abs(fig.rates.independence - base.rates.independence) > 0.0005 || Math.abs(fig.rates.everydayLiving - base.rates.everydayLiving) > 0.0005)
        ? <>{Their} contribution for each hour {fig.rates.independence + fig.rates.everydayLiving < base.rates.independence + base.rates.everydayLiving ? 'falls' : 'rises'}: getting out and about <strong>{pct(base.rates.independence)} → {pct(fig.rates.independence)}</strong>, help around the house <strong>{pct(base.rates.everydayLiving)} → {pct(fig.rates.everydayLiving)}</strong>.</>
        : null;
    const incomeLine = base && Math.abs(fig.investmentIncome - base.investmentIncome) >= 100
        ? <>Investment income {fig.investmentIncome < base.investmentIncome ? 'falls' : 'rises'} from {formatCurrency(Math.round(base.investmentIncome))} to {formatCurrency(Math.round(fig.investmentIncome))} a year{fig.investmentIncome < base.investmentIncome ? ' — the cost of the plan' : ''}.</>
        : null;
    const runway = projections && projections.length ? planRunwayCopy(projections as never, horizonYears) : null;
    // Every account in the order it is drawn, as every module's card says it (owner, 22 Oct 2026).
    const runLine = factFind && projections && projections.length ? runwayCardLine({ rows: projections as never, factFind, horizonYears, spentOn: 'the plan' }) : null;
    const explainer = SAH_STRATEGY_EXPLAINERS[planType];
    const pensionDelta = base ? fig.pension - base.pension : 0;
    const numbers: PlanCardNumber[] = [
        { label: `${Their} contribution to the support`, value: fig.contribution, unit: 'a year', vs: base ? `the support alone ${formatCurrency(Math.round(base.contribution))}` : '', onWhy: () => onWhy('contribution') },
        {
            label: 'Age Pension', value: fig.pension, unit: 'a year',
            vs: base && Math.abs(pensionDelta) >= 1
                ? <><span className={pensionDelta > 0 ? 'font-bold text-emerald-700' : 'font-bold text-rose-700'}>{pensionDelta > 0 ? '+' : '−'}{formatCurrency(Math.abs(Math.round(pensionDelta)))} a year</span> vs the support alone</>
                : 'the same as the support alone',
            tone: pensionDelta > 0 ? 'good' : 'neutral',
            onWhy: () => onWhy('pension'),
        },
        {
            label: fig.cashflow < 0 ? 'Each year, short by' : 'Left over each year', value: Math.abs(fig.cashflow),
            vs: base ? (base.cashflow < 0 ? `was short ${formatCurrency(Math.abs(Math.round(base.cashflow)))} with the support alone` : `with the support alone, ${formatCurrency(Math.round(base.cashflow))} to spare`) : '',
            tone: fig.cashflow >= 0 ? 'good' : 'neutral',
            onWhy: () => onWhy('cashflow'),
        },
    ];
    // A hand-built plan leads on nothing (owner, 25 Oct 2026: it read "leads on built by you"): its own words.
    const worse = legacyDelta != null && legacyDelta < -1
        ? <p><strong className="text-rose-700">Important:</strong> what {they} leave the family would be {formatCurrency(Math.abs(Math.round(legacyDelta)))} less than the support alone.{chip === 'Built by you' ? <> This is the plan {they} built; weigh that against what it does each year.</> : chip ? <> It is shown because it leads on {chip.toLowerCase()}; weigh that against what it does each year.</> : <> Weigh that against what it does each year.</>}</p>
        : null;
    return (
        <EnginePlanCard
            testId={testId}
            whyShown={tradeOffNote || null}
            chip={chip}
            chipTone="blue"
            title={title}
            sub={sub}
            legacy={fig.legacy}
            legacyDelta={legacyDelta}
            horizonYears={horizonYears}
            bars={legacyDelta != null ? { gainPct: (Math.abs(legacyDelta) / maxGain) * 100, tiedUp: moved.amount, tiedUpPct: (moved.amount / maxMoved) * 100 } : null}
            tiedUpLabel={moved.label}
            hideEmptyTiedUp
            linesBulleted
            numbers={numbers}
            runline={runway || familyPanel ? (
                <>
                    {runLine
                        ? <><strong className="text-slate-900">When the money runs out:</strong> {runLine}</>
                        : runway && <><strong className="text-slate-900">When the money runs out:</strong> {runway.headline.charAt(0).toLowerCase() + runway.headline.slice(1)}{runway.flag ? `. ${runway.flag}` : '.'}</>}
                    {familyPanel && <div className={runway ? 'mt-3' : ''}>{familyPanel}</div>}
                </>
            ) : null}
            worthKnowing={worse || (explainer?.consider?.[0] ? <p><strong className="text-slate-900">Worth knowing:</strong> {explainer.consider[0]}</p> : null)}
            lines={[rateLine, incomeLine].filter(Boolean) as React.ReactNode[]}
            chosen={chosen}
            onExplain={onExplain}
            onRisks={onRisks}
            onChoose={chooseDisabled || chooseHeld ? () => undefined : onChoose}
            beforeChoose={beforeChoose}
            chooseHeld={chooseHeld}
            chooseLabel={anotherChosen ? 'Choose this instead' : 'Choose this plan'}
            chosenStrip={<ChosenAdviceLine possessiveFile={possessiveFile} onAdvice={onAdvice} onCustomise={onBuildOn ?? null} />}
            extraAction={onCareUp ? (
                <button type="button" onClick={onCareUp} className="mt-3 self-start text-[13px] font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4 hover:decoration-brand-navy" data-testid="sah-care-up-open">If care needs increase</button>
            ) : null}
            detail={detail}
            detailOpen={detailOpen}
            onToggleDetail={onToggleDetail ?? (() => undefined)}
            detailHint="where the money comes from, the yearly figures and every calculation"
        />
    );
};

const SahPlansView = ({
    factFind, doNothing, recommended, strategies, waysTested, horizonYears, focus, onFocus, chosenName,
    onChoose, onExplain, onRisks, onWhy, onCareUp, renderDetail, onCompare, onAllWays, onSettings, onBuildYourOwn, onAdvice, notices, guaranteedIncomeFor,
}: SahPlansViewProps) => {
    const v = householdVoice(factFind);
    const [openDetail, setOpenDetail] = React.useState<string | null>(null);
    const base = doNothing?.snapshot ? figuresFromSnapshot(doNothing.snapshot) : null;
    const fullRow = (r: RecommendedStrategyEntry | null): SahPlanRow | null => {
        if (!r?.strategy?.name) return null;
        return (strategies.find((s) => s.name === r.strategy.name) || r.strategy) as SahPlanRow;
    };
    const coveredSame = coveredIsBestEstate(recommended);
    const effective: GoalFocusId = focus === 'covered' && coveredSame ? 'legacy' : focus;
    const leader = fullRow(goalLeaderFor(recommended, effective));
    const labelsFor = (row: SahPlanRow): string[] =>
        recommended.filter((r) => r.strategy?.name && r.strategy.name === row.name).map((r) => String(r.label));
    // A second card beside the estate leader, only for a real trade-off (planChoiceRule.ts, owner 20 Oct 2026).
    const secondEntry = effective === 'legacy' ? recommended.find((r) => r.label === NEAR_BEST_LABEL && r.second) ?? null : null;
    const secondRow = secondEntry ? fullRow(secondEntry) : null;
    // The estate leader, when it is not the plan that leaves the most, says so on its chip (rule 2).
    const estateEntry = recommended.find((r) => r.label === 'Optimal Legacy') ?? null;
    const chipFor = (l: string): string | undefined => (l === 'Optimal Legacy' ? leadChip(estateEntry?.lead) : l === NEAR_BEST_LABEL && secondEntry?.second ? secondPlanChip(secondEntry.second) : GOAL_CHIP[l]);
    const shown = leader ? [leader, ...(secondRow && secondRow.name !== leader.name ? [secondRow] : [])] : [];
    const goalWords = (g: 'legacy' | 'cashflow' | 'pension') => (g === 'cashflow' ? 'Most left over each year' : g === 'pension' ? 'The highest Age Pension' : 'Leaving the most to the family');
    // Another goal led by a different plan: one line pointing to it (the mock-up's "only one plan leads").
    const others = (['legacy', 'cashflow', 'pension'] as const)
        .filter((g) => g !== effective)
        .map((g) => ({ g, row: fullRow(goalLeaderFor(recommended, g)) }))
        .filter((x): x is { g: 'legacy' | 'cashflow' | 'pension'; row: SahPlanRow } => !!x.row && !shown.some((s) => s.name === x.row!.name));
    const chosenRow = chosenName ? strategies.find((s) => s.name === chosenName) || null : null;
    const chosenTitle = chosenRow ? sahPlanWords(chosenRow.config, factFind, chosenRow.name).title : null;
    const yrs = `${horizonYears} ${horizonYears === 1 ? 'year' : 'years'}`;
    const maxGain = Math.max(1, ...shown.map((s) => Math.abs((Number(s.snapshot?.legacyValue) || 0) - (base?.legacy ?? 0))));
    const maxMoved = Math.max(1, ...shown.map((s) => sahMovedAmount(s.config).amount));

    return (
        <div className="space-y-6" data-testid="sah-plans">
            {chosenRow && (
                <div data-testid="sah-chosen-banner" className="flex flex-wrap items-center gap-4 rounded-2xl border border-slate-200 border-l-4 border-l-emerald-500 bg-white px-5 py-4 shadow-card">
                    <span className="text-[26px] leading-none text-emerald-500" aria-hidden>✓</span>
                    <p className="min-w-0 flex-1 text-[14px] leading-relaxed text-slate-700">
                        <strong className="text-slate-900">You&apos;ve chosen &ldquo;{chosenTitle}&rdquo;.</strong> It&apos;s saved with {v.possessive} file, with the services it was priced on. The next step is the advice document — the written record of this plan: what it assumes, what it costs each year, and what could go wrong.
                    </p>
                    <button type="button" onClick={() => onBuildYourOwn(chosenRow)} className="text-[14px] font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4 hover:decoration-brand-navy">Customise this plan</button>
                </div>
            )}

            {notices}

            {base && (
                <DoNothingStrip
                    caption="Compared with the support alone — the four numbers from step 2"
                    careDaily={0}
                    careAnnual={0}
                    enteringName={v.names}
                    costCell={{ label: `${v.Their} contribution`, value: base.contribution, unit: 'a year', note: 'to the support, with nothing else changed' }}
                    pensionLabel="Age Pension"
                    pensionNoteText={v.couple ? `a year, the two of ${v.them}` : 'a year'}
                    pension={base.pension}
                    cashflowAfterCare={base.cashflow}
                    cashflowNotes={{ short: 'met from savings', over: 'after the support and living costs' }}
                    legacy={base.legacy}
                    horizonYears={horizonYears}
                    onWhyYears={onSettings}
                />
            )}

            <EngineGoalControl
                baselineWord={SAH_BENCH.word}
                value={focus}
                onChange={onFocus}
                household={v.names}
                enteringName={v.names}
                horizonYears={horizonYears}
                coveredSameAsBest={coveredSame}
                bestCovers={coveredSame && Number(fullRow(goalLeaderFor(recommended, 'legacy'))?.snapshot?.netCashflow ?? fullRow(goalLeaderFor(recommended, 'legacy'))?.snapshot?.cashflow ?? -1) >= 0}
                waysTested={waysTested}
                shown={shown.length}
                testedFor="to pay for the support"
                closingNote={<>Each card says when the savings would run out, and how much it lowers what {v.they} pay for each hour.</>}
                subs={{ covered: coveredSame ? 'Same plan as "Leave the most"' : 'Pays its way without savings' }}            />

            {shown.length === 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card" data-testid="sah-no-leader">
                    <h3 className="text-[19px] font-bold text-slate-900">No plan does better than the support alone on this goal</h3>
                    <p className="mt-2 text-[14px] leading-relaxed text-slate-700">
                        Every way tested would cost more than it adds here, so keeping things as they are is the best result for this goal.
                        {others.length > 0 && <> Another goal has a plan that helps — switch the goal above to see it.</>}
                    </p>
                </div>
            )}

            <div className={`grid grid-cols-1 items-start gap-5 ${shown.length > 1 ? 'lg:grid-cols-2' : ''}`} data-testid="sah-plan-cards">
                {chosenRow && !shown.some((s) => s.name === chosenRow.name) && (
                    <div className={shown.length > 1 ? 'lg:col-span-2' : ''}><ChosenAdviceLine possessiveFile={v.possessive} onAdvice={onAdvice} offCard testId="chosen-plan-off-card" /></div>
                )}
                {shown.map((row) => {
                    const fig = figuresFromSnapshot(row.snapshot);
                    const words = sahPlanWords(row.config, factFind, row.name);
                    const chip = labelsFor(row).map(chipFor).filter(Boolean).join(' · ') || null;
                    const isChosen = chosenName != null && row.name === chosenName;
                    const gi = guaranteedIncomeFor?.(row) ?? null;
                    return (
                        <SahPlanCard
                            key={String(row.name)}
                            factFind={factFind}
                            possessiveFile={v.possessive}
                            they={v.they}
                            Their={v.Their}
                            fig={fig}
                            base={base}
                            title={words.title}
                            sub={words.sub}
                            chip={chip}
                            moved={sahMovedAmount(row.config)}
                            scale={{ maxGain, maxMoved }}
                            horizonYears={horizonYears}
                            projections={row.projections}
                            planType={String(row.config?.type || '')}
                            chosen={isChosen}
                            anotherChosen={chosenName != null && !isChosen}
                            onExplain={() => onExplain(row)}
                            onRisks={() => onRisks(row)}
                            onChoose={() => onChoose(row)}
                            onWhy={(page) => onWhy(row, page)}
                            onAdvice={onAdvice}
                            onBuildOn={() => onBuildYourOwn(row)}
                            onCareUp={onCareUp ? () => onCareUp(row) : undefined}
                            detail={renderDetail(row)}
                            detailOpen={openDetail === row.name}
                            onToggleDetail={() => setOpenDetail((d) => (d === row.name ? null : String(row.name)))}
                            tradeOffNote={secondEntry?.second && row === secondRow ? secondPlanLine(secondEntry.second, formatCurrency) : estateEntry?.lead && row.name === estateEntry.strategy?.name ? leadLine(estateEntry.lead, formatCurrency) : null}
                            familyPanel={gi?.familyPanel}
                            beforeChoose={gi?.beforeChoose}
                            chooseHeld={!!gi?.chooseHeld && !isChosen}
                        />
                    );
                })}
                {shown.length > 0 && others.length > 0 && (
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-card" data-testid="sah-other-leaders">
                        {/*
                         * WHERE A DIFFERENT PLAN WINS (owner, 20 Oct 2026: "Another goal has a different plan"
                         * confused him). Said as the trade-off it is: which goal, that plan's figure against the
                         * one on screen, and what it costs the family.
                         */}
                        <h3 className="text-[17px] font-bold text-slate-900">{others.length === 1 ? ({ cashflow: 'If more to live on each year matters most', pension: 'If the Age Pension matters most', legacy: 'If leaving the most to the family matters most' } as const)[others[0].g] : 'Where a different plan does better'}</h3>
                        <p className="mt-1 text-[13px] text-slate-500">{others.length === 1 ? 'The plan on the left is the best for every other goal.' : 'The plan on the left is the best for the goal chosen above.'}</p>
                        <ul className="mt-2 space-y-3 text-[14px] leading-relaxed text-slate-700">
                            {others.map(({ g, row }) => {
                                const fig = figuresFromSnapshot(row.snapshot);
                                const here = shown[0] ? figuresFromSnapshot(shown[0].snapshot) : null;
                                const t = sahPlanWords(row.config, factFind, row.name).title;
                                const dl = base ? fig.legacy - base.legacy : 0;
                                const cf = (n: number) => (n >= 0 ? `${formatCurrency(Math.round(n))} a year to spare` : `${formatCurrency(Math.abs(Math.round(n)))} a year short`);
                                const fact = g === 'cashflow'
                                    ? <>{cf(fig.cashflow)}{here ? <>, against {cf(here.cashflow)} with the plan on the left</> : null}</>
                                    : g === 'pension'
                                        ? <>{formatCurrency(Math.round(fig.pension))} a year of Age Pension{here ? <>, against {formatCurrency(Math.round(here.pension))}</> : null}</>
                                        : <>{formatCurrency(Math.round(fig.legacy))} left to the family{here ? <>, against {formatCurrency(Math.round(here.legacy))}</> : null}</>;
                                return (
                                    <li key={g}>
                                        {others.length > 1 && <strong className="text-slate-900">{goalWords(g)}: </strong>}
                                        <strong className="text-slate-900">{t}</strong> gives {fact}.
                                        {base && Math.abs(dl) >= 1 && g !== 'legacy' && <> But it leaves the family {formatCurrency(Math.abs(Math.round(dl)))} {dl >= 0 ? 'more' : 'less'} than the support alone.</>}{' '}
                                        <button type="button" onClick={() => onFocus(g)} className="font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4">See that plan</button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-3" data-testid="sah-ways-on">
                <button type="button" onClick={onCompare} className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[14px] font-semibold text-slate-800 hover:border-blue-300">Compare the plans side by side</button>
                <button type="button" onClick={onAllWays} className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[14px] font-semibold text-slate-800 hover:border-blue-300">All {waysTested.toLocaleString()} ways tested</button>
                <button type="button" onClick={onSettings} className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[14px] font-semibold text-slate-800 hover:border-blue-300">Things that change the answer</button>
                <button type="button" onClick={() => onBuildYourOwn(null)} className="text-[14px] font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4 hover:decoration-brand-navy" data-testid="sah-build-your-own">Prefer to set the numbers yourself? Build a plan →</button>
            </div>
            <p className="text-[13px] text-slate-500" data-testid="all-plans-open-line">{allPlansOpenLine(waysTested)}</p>
            <p className="text-[13px] text-slate-500">Figures use the rates in force today and are projections over {yrs}, not guarantees.</p>
        </div>
    );
};

export default SahPlansView;
